import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { LoadingController, ModalController, Platform } from "@ionic/angular";
import {
  FileSystemDirectoryEntry,
  FileSystemFileEntry,
  NgxFileDropEntry,
} from "ngx-file-drop";
import { AnimationOptions } from 'ngx-lottie';
import { Settings } from 'src/app/shared-lib/core/settings';
import { PrintFileStatus } from 'src/app/shared-lib/domains/product';
import { AlertModalComponent } from "../../../components/alert-modal/alert-modal.component";
import { CartService } from "../../../shared/cart.service";
import { FileService } from "../../../shared/file.service";
import { DropboxPickerService } from "../../../shared/services/dropbox-picker.service";
import { GoogleDrivePickerService } from "../../../shared/services/googledrivepicker.service";
import { UploadingFilesModalComponent } from "../../uploading-files-modal/uploading-files-modal.component";

declare var google: any;
declare var Dropbox: any;

@Component({
  selector: "app-upload-files-options",
  templateUrl: "./upload-files-options.component.html",
  styleUrls: ["./upload-files-options.component.scss"],
  standalone: false
})
export class UploadFilesOptionsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("fileDropRef", { static: false }) fileDropRef: ElementRef;
  @Input() printProduct;
  @Input() files;
  @Input() fromBar = false;
  loading: HTMLIonLoadingElement;
  pdfToConvert;
  dragOver = false;
  draggingFilesAnimationOptions: AnimationOptions = {
    path: '/assets/lotties/draggingFilesPlane.json',
    autoplay: true,
    loop: true,
  };
  public filesDrag: NgxFileDropEntry[] = [];
  private dropboxSubscription: any;
  private mainContentObserver: ResizeObserver;

  get dragging() {
    return this.dragOver;
  }

  constructor(
    private googleDrivePickerService: GoogleDrivePickerService,
    private dropboxPickerService: DropboxPickerService,
    private platform: Platform,
    private cartService: CartService,
    private modalController: ModalController,
    private loadingController: LoadingController,
    private fileService: FileService,
    private hostEl: ElementRef<HTMLElement>
  ) { }

  ngAfterViewInit() {
    const mainContent = this.hostEl.nativeElement.closest('.mainContent') as HTMLElement ?? this.hostEl.nativeElement;
    this.updateNotificationPosition(mainContent);
    this.mainContentObserver = new ResizeObserver(() => this.updateNotificationPosition(mainContent));
    this.mainContentObserver.observe(mainContent);
  }

  private updateNotificationPosition(mainContent: HTMLElement) {
    const rect = mainContent.getBoundingClientRect();
    const host = this.hostEl.nativeElement;
    host.style.setProperty('--main-content-left', `${rect.left}px`);
    host.style.setProperty('--main-content-right', `${window.innerWidth - rect.right}px`);
  }

  async ngOnInit() {
    this.loading = await this.loadingController.create({
      cssClass: "my-custom-class",
      message: "Cargando archivos...",
      spinner: "bubbles",
    });
  }

  openGoogleDrivePicker(): void {
    this.googleDrivePickerService.open(async (data) => {
      if (data[google.picker.Response.ACTION] === google.picker.Action.PICKED) {
        this.loading.present();
        const docs = data[google.picker.Response.DOCUMENTS];
        try {
          const files = await this.googleDrivePickerService.getFiles(docs);
          this.loading.dismiss();
          this.fileBrowseHandler(files);
        } catch (err) {
          this.loading.dismiss();
          console.error('Error downloading files from Google Drive:', err);
          const modal = await this.modalController.create({
            component: AlertModalComponent,
            cssClass: 'auto-height',
            componentProps: {
              msg: err?.message || 'No se pudieron descargar los archivos desde Google Drive. Inténtalo de nuevo.',
            },
          });
          await modal.present();
        }
      }
    });
  }

  openDropboxPicker() {
    if (this.dropboxSubscription) {
      this.dropboxSubscription.unsubscribe();
    }

    this.dropboxPickerService.open();
    this.dropboxSubscription = this.dropboxPickerService
      .getResponse()
      .subscribe((response) => {
        if (response && response.length > 0) {
          this.fileBrowseHandler(response);
          if (this.dropboxSubscription) {
            this.dropboxSubscription.unsubscribe();
            this.dropboxSubscription = null;
          }
        }
      });
  }
  isDropboxSupported() {
    try {
      return Dropbox.isBrowserSupported();
    } catch (e) {
      return false;
    }
  }

  async fileBrowseHandler(_files) {
    if (this.cartService.cart.items.some((it) => it.product.presale)) {
      const modal = await this.modalController.create({
        component: AlertModalComponent, // AddressComponent,
        componentProps: {
          msg: "Actualmente tu carrito contiene productos en preventa que se encuentran en proceso de producción por lo que para respetar los tiempos de entrega no es posible añadir impresiones. Por favor realiza 2 pedidos independientes.",
        },
        cssClass: "auto-height",
      });
      await modal.present();
      return;
    }
    let files = this.fileDropRef.nativeElement.files;
    if (files.length === 0 && _files) {
      files = _files;
    }
    // fileBrowseHandler(files){// este files representa los archivos seleccionados para añadir
    //open modal
    const uploadingModal = await this.modalController.create({
      component: UploadingFilesModalComponent,
      backdropDismiss: false,
      componentProps: {
        files: this.files,
      },
      cssClass: "auto-height medium-width",
    });

    uploadingModal.present();

    uploadingModal.onDidDismiss().then((data) => {
      if (data.role === "cancel") {
        this.cancelAllUploads();
      }
    });

    for (let index = 0; index < files.length; index++) {
      const file = files[index];

      if (file.size > 300 * 1024 * 1024) {
        alert(file.name + ", no se puede subir, excede los 300 MB.");
      } else if (file.type != "application/pdf") {
        alert(file.name + ", solo se permiten archivos PDF.");
      } else {
        this.files.push({
          // este es el almacenamiento de los files
          file, // este es el archivo actual a añadir en el almacenamiento de files
          isUploaded: false,
          progress: 0,
        });
        this.printProduct.product.printingGroup.files.push({
          id: null,
          originalFile: null,
          originalType: null,
          printFile: null,
          pages: 0,
          thumbFile: null,
          status: PrintFileStatus.NEW,
          coverColor: false,
          coverLaminated: false,
          docColor: false,
          laminationType:
            this.printProduct.product.printingGroup.printSettings[
            Settings.LAMINATION_TYPE
            ],
          laminationWeight:
            this.printProduct.product.printingGroup.printSettings[
            Settings.LAMINATION_WEIGHT
            ],
        });
      }
    }
    this.fileDropRef.nativeElement.value = "";
    //activamos la generación de imágenes
    this.onPdfConverted();
  }

  onPdfConverted(event = null) {
    if (event) {
      const { type, payload } = event;
      const file = this.files.find((f) => f.file == event.payload.file);
      file.pdfImages = payload.pdfImages;
    }
    const fileNoThumb = this.files.find((f) => f.file && !f.pdfImages);
    if (fileNoThumb) {
      this.pdfToConvert = fileNoThumb.file;
    }
  }

  public dropped(files: NgxFileDropEntry[]) {
    this.filesDrag = files;
    const filesToAdd = [];
    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          this.fileBrowseHandler([file]);
        });
      } else {
        // It was a directory (empty directories are added, otherwise only files)
        const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
      }
    }
    this.dragOver = false;
  }

  public fileOver(event) {
    this.dragOver = true;
  }

  public fileLeave(event) {
    this.dragOver = false;
  }

  public isDesktop() {
    return this.platform.is("desktop") || this.isWindows();
  }

  private isWindows() {
    if (!window || !navigator) {
      return false;
    }

    const a: string =
      navigator.userAgent || navigator.vendor || (window as any).opera;

    return /windows/i.test(a);
  }

  private cancelAllUploads() {
    this.fileService.triggerCancelUploads();
  }

  ngOnDestroy() {
    this.mainContentObserver?.disconnect();
    // Limpiar suscripción al destruir el componente
    if (this.dropboxSubscription) {
      this.dropboxSubscription.unsubscribe();
    }
    this.dropboxPickerService.reset();
  }
}

